import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Bank } from '@tink/core/src/components/atoms/Icons/Bank';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { Stack } from '../../../components/atoms/Stack/Stack';
import { H1, H2 } from '../../../components/atoms/Heading/Heading';
import { Button } from '../../../components/atoms/Button/Button';
import { resetCredentials } from '../../../stores/credentials-store';
import { buildHomeUrlWithConnectedProviders } from '../../../routes';
import { useProduct } from '../../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { useVersion } from '../../../components/ApplicationProviders/VersionProvider/VersionProvider';
import { useSelectedProvider } from '../../../components/App/ProviderContainer/useSelectedProvider';

import { AppState } from '../../../stores/redux-store';
import { FlexBox } from '../../../components/atoms/FlexBox/FlexBox';
import { Footer } from '../../../components/atoms/Footer/Footer';
import { messages } from './messages';
import {
  LogoRow,
  PlusSign,
  ProviderLogo,
  RoundedIcon,
} from './MultibankingAddProviderScreen.styles';
import { useConnectedProviders, useMultiBankingUser } from './multibankingUtils';

export type MultibankingAddProviderScreenProps = {
  onDoneAddingBanks: () => void;
};

export const MultibankingAddProviderScreen = ({
  onDoneAddingBanks,
}: MultibankingAddProviderScreenProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { productType, productDimension } = useProduct();
  const {
    version: { version },
  } = useVersion();

  let multiBankingUser = useMultiBankingUser();

  const { selectedProvider: provider } = useSelectedProvider();

  const providers = useSelector((state: AppState) => state.providers.data);
  const user = useSelector((state: AppState) => state.user.user);

  let connectedProviders = useConnectedProviders();

  if (!connectedProviders) {
    connectedProviders = provider.name;
  } else if (!connectedProviders.includes(provider.name)) {
    connectedProviders += `,${provider.name}`;
  }

  const filteredData = providers?.filter((obj) =>
    connectedProviders?.split(',').includes(obj.name)
  );

  const icons =
    filteredData && filteredData.map((obj) => ({ icon: obj.images?.icon, name: obj.name }));

  if (!multiBankingUser && user) {
    multiBankingUser = user?.id;
  }

  const onConfirmAddBank = () => {
    /*
     * In Case the user wants to add another bank,
     * we have to clear the credentials first, otherwise
     * the component rendering that state will advance again
     * immediately due to credentials already existing.
     */
    dispatch(resetCredentials());

    if (connectedProviders && multiBankingUser) {
      history.push(
        buildHomeUrlWithConnectedProviders({
          version,
          productType,
          productDimension,
          connectedProviders,
          multiBankingUser,
        })
      );
    }
  };

  return (
    <ScreenWrapper>
      <Stack justifyContent="center" alignItems="center">
        <Box px="xxsmall" pb="normal">
          <FlexBox direction="row">
            <LogoRow>
              {icons?.map(({ icon, name }) => {
                return <ProviderLogo key={name} src={icon} />;
              })}
            </LogoRow>
            <PlusSign>+</PlusSign>
            <RoundedIcon>
              <Bank />
            </RoundedIcon>
          </FlexBox>
        </Box>
        <Box px="xxsmall" pb="normal">
          <H1 align="center">
            <FormattedMessage {...messages.title} />
          </H1>
          <H2 regularFontWeight color="secondaryTextColor" align="center">
            <FormattedMessage {...messages.subTitle} />
          </H2>
        </Box>
      </Stack>
      <Footer stickToBottom>
        <Button
          size="large"
          type="submit"
          color="primary"
          variant="contained"
          onClick={onConfirmAddBank}
        >
          <FormattedMessage {...messages.addBank} />
        </Button>
        <Button size="large" color="primary" variant="text" onClick={onDoneAddingBanks}>
          <FormattedMessage {...messages.noThanks} />
        </Button>
      </Footer>
    </ScreenWrapper>
  );
};
